import { useEffect, useState } from "react";
import { Edit, KeyRound, Shield, Trash2, UserCog } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const USER_ROLES = ["admin", "manager", "viewer"];
const USER_STATUSES = ["Active", "Inactive"];

export const UserManager = ({ open, onClose, users, companies = [], onCreate, onUpdate, onDelete }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSecret, setLastSecret] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    display_name: "",
    password: "",
    role: "manager",
    status: "Active",
    email: "",
    manageable_company_ids: [],
    manageable_project_ids: [],
  });

  useEffect(() => {
    if (!editingUser) {
      setFormData({
        username: "",
        display_name: "",
        password: "",
        role: "manager",
        status: "Active",
        email: "",
        manageable_company_ids: [],
        manageable_project_ids: [],
      });
      return;
    }
    setFormData({
      username: editingUser.username || "",
      display_name: editingUser.display_name || "",
      password: "",
      role: editingUser.role || "manager",
      status: editingUser.status || "Active",
      email: editingUser.email || "",
      manageable_company_ids: editingUser.manageable_company_ids || [],
      manageable_project_ids: editingUser.manageable_project_ids || [],
    });
  }, [editingUser, open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.username.trim()) return;
    setSubmitting(true);
    try {
      let result;
      if (editingUser) {
        result = await onUpdate(editingUser.id, {
          display_name: formData.display_name.trim(),
          password: formData.password,
          role: formData.role,
          status: formData.status,
          email: formData.email.trim(),
          manageable_company_ids: formData.manageable_company_ids,
          manageable_project_ids: formData.manageable_project_ids,
        });
      } else {
        result = await onCreate({
          username: formData.username.trim(),
          display_name: formData.display_name.trim(),
          password: formData.password,
          role: formData.role,
          status: formData.status,
          email: formData.email.trim(),
          manageable_company_ids: formData.manageable_company_ids,
          manageable_project_ids: formData.manageable_project_ids,
        });
      }
      setLastSecret(result?.totp_secret || "");
      setEditingUser(null);
    } finally {
      setSubmitting(false);
    }
  };

  const allProjects = companies.flatMap((company) =>
    (company.businesses || []).flatMap((business) => (business.projects || []).map((project) => ({
      id: project.id,
      name: project.name,
      company_name: company.name,
    }))),
  );

  const toggleSelection = (key, value) => {
    setFormData((current) => ({
      ...current,
      [key]: current[key].includes(value) ? current[key].filter((item) => item !== value) : [...current[key], value],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">Users & Roles</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(340px,0.85fr)] gap-6 mt-4">
          <div className="border border-zinc-200">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <UserCog className="text-zinc-500" size={16} />
              <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Accounts</h3>
            </div>
            <div className="divide-y divide-zinc-200 max-h-[460px] overflow-y-auto">
              {users.length === 0 ? (
                <div className="p-6 text-sm text-zinc-400">No users yet.</div>
              ) : (
                users.map((user) => (
                  <div className="flex items-start justify-between gap-4 p-4" key={user.id}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-zinc-900">{user.display_name || user.username}</p>
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                          <Shield size={12} /> {user.role}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500">@{user.username}</p>
                      <p className="text-sm text-zinc-400">{user.email || "No email"}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">{user.status}</span>
                      <button className="text-zinc-400 hover:text-zinc-700" onClick={() => setEditingUser(user)} type="button">
                        <Edit size={14} />
                      </button>
                      <button className="text-zinc-400 hover:text-red-600" onClick={() => onDelete(user.id)} type="button">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <form className="space-y-4 border border-zinc-200 p-5 bg-zinc-50/40" onSubmit={handleSubmit}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">{editingUser ? "Edit User" : "Create User"}</h3>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Username *</Label>
              <Input className="rounded-none border-zinc-300 bg-white" disabled={!!editingUser} onChange={(event) => setFormData({ ...formData, username: event.target.value })} value={formData.username} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Display Name</Label>
              <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, display_name: event.target.value })} value={formData.display_name} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{editingUser ? "Reset Password" : "Password *"}</Label>
              <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, password: event.target.value })} type="password" value={formData.password} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Role</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, role: value })} value={formData.role}>
                  <SelectTrigger className="rounded-none border-zinc-300 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{USER_ROLES.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Status</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, status: value })} value={formData.status}>
                  <SelectTrigger className="rounded-none border-zinc-300 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{USER_STATUSES.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Email</Label>
              <Input className="rounded-none border-zinc-300 bg-white" onChange={(event) => setFormData({ ...formData, email: event.target.value })} type="email" value={formData.email} />
            </div>
            {formData.role === "manager" ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Manager Company Scope</Label>
                  <div className="max-h-28 overflow-y-auto border border-zinc-200 bg-white p-3 space-y-2">
                    {companies.length === 0 ? <p className="text-sm text-zinc-400">No companies available.</p> : companies.map((company) => (
                      <label className="flex items-center gap-2 text-sm text-zinc-700" key={company.id}>
                        <input checked={formData.manageable_company_ids.includes(company.id)} onChange={() => toggleSelection("manageable_company_ids", company.id)} type="checkbox" />
                        <span>{company.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Manager Project Scope</Label>
                  <div className="max-h-28 overflow-y-auto border border-zinc-200 bg-white p-3 space-y-2">
                    {allProjects.length === 0 ? <p className="text-sm text-zinc-400">No projects available.</p> : allProjects.map((project) => (
                      <label className="flex items-center gap-2 text-sm text-zinc-700" key={project.id}>
                        <input checked={formData.manageable_project_ids.includes(project.id)} onChange={() => toggleSelection("manageable_project_ids", project.id)} type="checkbox" />
                        <span>{project.name} · {project.company_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
            {lastSecret ? (
              <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <div className="flex items-center gap-2 font-semibold"><KeyRound size={14} /> TOTP Secret</div>
                <p className="mt-2 font-mono break-all">{lastSecret}</p>
              </div>
            ) : null}
            <div className="flex flex-wrap justify-end gap-3 pt-3">
              {editingUser ? (
                <Button className="rounded-none border-zinc-300" onClick={() => setEditingUser(null)} type="button" variant="outline">
                  Cancel Edit
                </Button>
              ) : null}
              <Button className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-6 text-xs font-bold uppercase tracking-wider" disabled={submitting} type="submit">
                {submitting ? "Saving..." : editingUser ? "Update User" : "Create User"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
